import React, { useState } from "react";
import "./PackingDashboard.css";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import Box from "../../../images/boxes.svg";
import BoxGreen from "../../../images/box_green.svg";

import TotalCase from "../../../images/totalCase.svg";
import TotalTrey from "../../../images/total_tray.svg";
import Carts from "../../../images/total_cart.svg";
import CartNo from "../../../images/cart_no.svg";
import KgGreen from "../../../images/kg_green.svg";
import TotalEmp from "../../../images/total_emp.svg";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { CartIconSvg } from "../../../svgFiles/CartIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { UsersIconSvg } from "../../../svgFiles/UsersIconSvg";
import { BarChartIconSvg } from "../../../svgFiles/BarChartIconSvg";
import { TrendingUpIconSvg } from "../../../svgFiles/TrendingUpIconSvg";
import { SortingIconSvg } from "../../../svgFiles/SortingIconSvg";

const PackingDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [isSingleDay, setIsSingleDay] = useState(true);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  // MetricBox component for card display
  const MetricBox = ({ label, value, unit, theme = "orange" }) => (
    <div className={`pko-box pko-box-${theme}`}>
      <div className="pko-box-label">{label}</div>
      <div className="pko-box-value">{value ?? "—"}</div>
      {unit && <div className={`pko-box-unit pko-unit-${theme}`}>{unit}</div>}
    </div>
  );

  const formatValueWithUnit = (label, value) => {
    const displayValue = value ?? 0;
    const strValue = String(displayValue);

    if (/%/.test(label)) {
      return /%/.test(strValue) ? strValue : `${strValue} %`;
    }
    if (/per\s*kg|\/\s*kg/i.test(label)) {
      return /per\s*kg/i.test(strValue) ? strValue : `${strValue} Per KG`;
    }
    if (/\bkg\b|kilo|weight/i.test(label)) {
      return /\bkg\b/i.test(strValue) ? strValue : `${strValue} KG`;
    }
    return strValue;
  };

  const handleApplyRange = ({ startDate, endDate }) => {
    const single = startDate === endDate;
    setIsSingleDay(single);
    if (single) setLotNumber(makePriorLotNumber(startDate));
  };

  // ── Section 1: Packing (Day) ─────────────────────────────────
  const packingMangoDayItems = [
    { label: "Total Boxes", value: 0, icon: BoxGreen },
    { label: "Cheeks 20 LB1", value: 0, icon: BoxGreen },
    { label: "Cheeks 10 LB", value: 0, icon: BoxGreen },
    { label: "Strips 20 LB", value: 0, icon: Box },
    { label: "Pasting 20LB", value: 0, icon: Box },
  ];

  // ── Section 2: Packing (Turn 2) ─────────────────────────────
  const packingMangoTurn2Items = [
    { label: "Total Boxes", value: 0, icon: TotalCase },
    { label: "Prime Cheek Boxes (10 pound)", value: 0, icon: BoxGreen },
    { label: "Prime Cheek Boxes (20 pound)", value: 0, icon: BoxGreen },
    { label: "Prime Strip Boxes (20 pound)", value: 0, icon: BoxGreen },
    { label: "Seconds Cheek Boxes (20 pound)", value: 0, icon: Box },
    { label: "Seconds Strip Boxes (20 pound)", value: 0, icon: Box },
    { label: "Tapitas Boxes (20 pound)", value: 0, icon: Box },
    { label: "Pieces Boxes (20 pound)", value: 0, icon: Box },
  ];

  const getTurn2MetricColor = (label) => {
    if (/seconds/i.test(label)) return "red";
    if (/total/i.test(label)) return "orange";
    return "green";
  };

  // ── Section 3: Carts + KG Mango Night ────────────────────────
  const enteredCompletedTreysItems = [
    { label: "Cheeks Tray Completed", value: 0, icon: TotalTrey },
    { label: "Tapita Tray Completed", value: 0, icon: TotalTrey },
    { label: "Strips Tray Completed", value: 0, icon: TotalTrey },
    { label: "Total Cart Completed", value: 0, icon: TotalTrey },
  ];

  const kgBaggedItems = [
    { label: "Cheeks KG Bagged", value: 0, icon: KgGreen },
    { label: "Tapita KG Bagged", value: 0, icon: KgGreen },
    { label: "Strips KG Bagged", value: 0, icon: KgGreen },
    { label: "Total KG Bagged", value: 0, icon: KgGreen },
  ];

  const sortingItems = [
    { label: "Total Prime KG", value: 0, icon: KgGreen },
    { label: "Prime Cheeks KG", value: 0, icon: KgGreen },
    { label: "Prime Strips KG", value: 0, icon: KgGreen },
    { label: "Rework Cheeks KG", value: 0, icon: KgGreen },
    { label: "Rework Strips KG", value: 0, icon: KgGreen },
  ];

  const packingItems = [
    { label: "Prime Cheeks Boxes (10lb)", value: 0, icon: BoxGreen },
    { label: "Prime Cheeks Boxes (20lb)", value: 0, icon: BoxGreen },
    { label: "Prime Strips Boxes", value: 0, icon: BoxGreen },
    { label: "Total Boxes", value: 0, icon: Box },
  ];

  const metricsItems = [
    { label: "Prime Cheeks %", value: 0 },
    { label: "Prime Strips %", value: 0 },
    { label: "Total Prime %", value: 0 },
  ];

  return (
    <div>
      {/* ── Toolbar ───────────────────────────────────────────── */}
      <div className="packing_dashboard_toolbar mb-4 align-items-center">
        <h5 className="packing_dashboard_toolbar_title m-0">
          {_t("packing_overview", "Packing Overview")}
        </h5>

        <div className="packing_toolbar_right">
          {isSingleDay && (
            <div className="packing_filter_group">
              <label className="packing_filter_label">
                {_t("lot_number", "Lot Number")}
              </label>
              <div className="packing_lot_number_row">
                <div className="packing_lot_chip borderRedBtn cmn_btn btn-small">
                  #{lotNumber}
                </div>
              </div>
            </div>
          )}

          <div className="packing_filter_group">
            <label
              htmlFor="packing-date-range-filter"
              className="packing_filter_label"
            >
              {_t("date_range", "Date Range")}
            </label>
            <DateRangeFilter
              setDate={setDate}
              onApplyRange={handleApplyRange}
              triggerId="packing-date-range-filter"
            />
          </div>
        </div>
      </div>

      {/* ── Section 1 ─────────────────────────────────────────── */}
      <div>
        {/* Zone 1: Entered/Completed Treys */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <PackageIconSvg size={18} color="#fb923c" />
              {_t("entered_completed_treys", "Entered/Completed Treys")}
            </span>
          }
        >
          <div className="pko-grid-5 p-0">
            {enteredCompletedTreysItems.map((item, i) => {
              const unitMatch = item.label.match(/(\d+\s*LB)|(10\s*pound)|(20\s*pound)/i);
              const unit = unitMatch ? unitMatch[0] : null;
              const labelClean = item.label.replace(/(\d+\s*LB)|(10\s*pound)|(20\s*pound)/i, "").trim().toUpperCase();
              return (
                <MetricBox
                  key={i}
                  label={_t(item.label, labelClean)}
                  value={item.value}
                  unit={unit}
                  theme="orange"
                />
              );
            })}
          </div>
        </DashboardSection>

        {/* Zone 2 & 3: KG Bagged & Sorting (Side by Side 50-50) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
          {/* Zone 2: KG Bagged */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <ScaleIconSvg size={18} color="#4ade80" />
                {_t("kg_bagged", "KG Bagged")}
              </span>
            }
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
              {kgBaggedItems.map((item, i) => (
                <div
                  key={i}
                  style={{
                    backgroundColor: "#f9fafb",
                    border: "1px solid #f0f1f3",
                    borderRadius: "8px",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div style={{ fontSize: "11px", fontWeight: "600", color: "#667085", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {_t(item.label, item.label)}
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#4ade80" }}>
                    {formatValueWithUnit(item.label, item.value)}
                  </div>
                </div>
              ))}
            </div>
          </DashboardSection>

          {/* Zone 3: Sorting */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <TrendingUpIconSvg size={18} color="#fb923c" />
                {_t("sorting", "Sorting")}
              </span>
            }
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
              {sortingItems.map((item, i) => (
                <div
                  key={i}
                  style={{
                    backgroundColor: "#f9fafb",
                    border: "1px solid #f0f1f3",
                    borderRadius: "8px",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div style={{ fontSize: "11px", fontWeight: "600", color: "#667085", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {_t(item.label, item.label)}
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#fb923c" }}>
                    {formatValueWithUnit(item.label, item.value)}
                  </div>
                </div>
              ))}
            </div>
          </DashboardSection>
        </div>

        {/* Zone 4: Packing */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <PackageIconSvg size={18} color="#fb923c" />
              {_t("packing", "Packing")}
            </span>
          }
        >
          <div className="pko-grid-5 p-0">
            {packingItems.map((item, i) => {
              const unitMatch = item.label.match(/\((\d+\s*lb|10\s*pound|20\s*pound)\)/i);
              const unit = unitMatch ? unitMatch[1] : null;
              const labelClean = item.label.replace(/\s*\(\d+\s*lb\)|\s*\(10\s*pound\)|\s*\(20\s*pound\)/i, "").trim().toUpperCase();
              return (
                <MetricBox
                  key={i}
                  label={_t(item.label, labelClean)}
                  value={item.value}
                  unit={unit}
                  theme="orange"
                />
              );
            })}
          </div>
        </DashboardSection>

        {/* Zone 5: Packing (Turn 2) */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <CycleIconSvg size={18} color="#60a5fa" />
              {_t("packing_turn_2", "Packing (Turn 2)")}
            </span>
          }
        >
          <div className="pko-turn2-layout p-0">
            {/* Left: Total Boxes tall cell */}
            <div className="pko-total-cell pko-box-blue">
              <div className="pko-box-label">TOTAL BOXES<br></br>(DAY & NIGHT)</div>
              <div className="pko-box-value">0</div>
            </div>
            {/* Right: Grid of box types */}
            <div className="pko-grid-right">
              {packingMangoTurn2Items.map((item, i) => {
                const unitMatch = item.label.match(/(\(\d+\s*pound\))/);
                const unit = unitMatch ? unitMatch[0].replace(/[()]/g, "") : null;
                const labelClean = item.label.replace(/\s*\(\d+\s*pound\)/, "").trim().toUpperCase();
                return (
                  <MetricBox
                    key={i}
                    label={_t(item.label, labelClean)}
                    value={item.value}
                    unit={unit}
                    theme="blue"
                  />
                );
              })}
            </div>
          </div>
        </DashboardSection>

        {/* Zone 6: Metrics */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <BarChartIconSvg size={18} color="#60a5fa" />
              {_t("metrics", "Metrics")}
            </span>
          }
        >
          <div className="db-inner-metric-row p-0">
            {metricsItems.map((item, i) => (
              <div key={i} className="db-inner-metric">
                <div className="db-metric-label">
                  {_t(item.label, item.label)}
                </div>
                <div className="db-metric-value">
                  {formatValueWithUnit(item.label, item.value)}
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default PackingDashboard;
